'use client';

import React, { useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Pencil, Pause, Play, Trash2, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import Badge from '@/components/ui/Badge';
import EmptyState from '@/components/ui/EmptyState';
import { Campaign } from './CampaignManagementContent';
import { Megaphone } from 'lucide-react';

interface Props {
  campaigns: Campaign[];
}

type SortKey = 'name' | 'client' | 'budget' | 'spend' | 'roas' | 'ctr' | 'impressions' | 'reach';

const PAGE_SIZE = 8;

export default function CampaignTable({ campaigns }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>('roas');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  const sorted = [...campaigns].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
  });

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
    setPage(1);
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === paginated.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(paginated.map((c) => c.id)));
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ArrowUpDown size={12} className="text-muted-foreground/50" />;
    return sortDir === 'asc' ? <ArrowUp size={12} className="text-primary" /> : <ArrowDown size={12} className="text-primary" />;
  };

  const SpendBar = ({ spend, budget }: { spend: number; budget: number }) => {
    const pct = budget > 0 ? Math.min(100, (spend / budget) * 100) : 0;
    const color = pct >= 90 ? 'bg-negative' : pct >= 70 ? 'bg-warning' : 'bg-primary';
    return (
      <div className="space-y-1">
        <div className="flex justify-between text-[10px] tabular-nums">
          <span className="text-muted-foreground">${spend.toLocaleString()}</span>
          <span className="text-muted-foreground">${budget.toLocaleString()}</span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div className={`h-full pacing-bar-fill ${color}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };

  if (campaigns.length === 0) {
    return (
      <div className="card-base">
        <EmptyState
          icon={Megaphone}
          title="No campaigns match your filters"
          description="Try adjusting your search or filter criteria to find the campaigns you're looking for."
          action={{ label: 'Clear Filters', onClick: () => {} }}
        />
      </div>
    );
  }

  return (
    <div className="card-base overflow-hidden">
      {/* Bulk Action Bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-primary/5 border-b border-primary/20 slide-up">
          <span className="text-sm font-medium text-primary">{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <button onClick={() => { toast.success(`${selected.size} campaigns paused`); setSelected(new Set()); }} className="btn-ghost text-xs py-1">
              <Pause size={12} /> Pause Selected
            </button>
            <button onClick={() => { toast.success(`${selected.size} campaigns resumed`); setSelected(new Set()); }} className="btn-ghost text-xs py-1">
              <Play size={12} /> Resume Selected
            </button>
            <button onClick={() => { toast.error(`Delete ${selected.size} campaigns? This cannot be undone.`); }} className="btn-ghost text-xs py-1 text-negative hover:text-negative"><Trash2 size={12} /> Delete Selected
            </button>
          </div>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-muted-foreground hover:text-foreground">Deselect all</button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="px-4 py-3 text-left w-10">
                <input
                  type="checkbox"
                  checked={selected.size === paginated.length && paginated.length > 0}
                  onChange={toggleAll}
                  className="w-4 h-4 rounded border-input accent-primary"
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button onClick={() => toggleSort('name')} className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
                  Campaign <SortIcon col="name" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button onClick={() => toggleSort('client')} className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
                  Client <SortIcon col="client" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Platform</span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</span>
              </th>
              <th className="px-4 py-3 text-left min-w-[160px]">
                <button onClick={() => toggleSort('spend')} className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
                  Spend / Budget <SortIcon col="spend" />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button onClick={() => toggleSort('roas')} className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors ml-auto">
                  ROAS <SortIcon col="roas" />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button onClick={() => toggleSort('ctr')} className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors ml-auto">
                  CTR <SortIcon col="ctr" />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button onClick={() => toggleSort('impressions')} className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors ml-auto">
                  Impressions <SortIcon col="impressions" />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button onClick={() => toggleSort('reach')} className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors ml-auto">
                  Reach <SortIcon col="reach" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Dates</span>
              </th>
              <th className="px-4 py-3 text-left w-10">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginated.map((c) => (
              <tr key={c.id} className="table-row-hover group">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggleSelect(c.id)}
                    className="w-4 h-4 rounded border-input accent-primary"
                  />
                </td>
                <td className="px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-foreground max-w-[200px] truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{c.objective} · {c.adSets} ad sets</p>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-[9px] font-bold text-primary">{c.client.charAt(0)}</span>
                    </div>
                    <span className="text-sm text-foreground whitespace-nowrap">{c.client}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 flex-wrap">
                    {(c.platform === 'facebook' || c.platform === 'both') && (
                      <Badge variant="facebook">FB</Badge>
                    )}
                    {(c.platform === 'instagram' || c.platform === 'both') && (
                      <Badge variant="instagram">IG</Badge>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Badge variant={c.status} dot>{c.status.charAt(0).toUpperCase() + c.status.slice(1)}</Badge>
                </td>
                <td className="px-4 py-3 min-w-[160px]">
                  {c.status === 'draft' || c.status === 'review' ? (
                    <span className="text-xs text-muted-foreground">Not started</span>
                  ) : (
                    <SpendBar spend={c.spend} budget={c.budget} />
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {c.roas > 0 ? (
                    <span className={`text-sm font-bold tabular-nums ${c.roas >= 3.0 ? 'text-positive' : c.roas >= 2.0 ? 'text-warning' : 'text-negative'}`}>
                      {c.roas.toFixed(1)}×
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {c.ctr > 0 ? (
                    <span className={`text-sm tabular-nums ${c.ctr >= 0.8 ? 'text-foreground' : 'text-negative font-medium'}`}>
                      {c.ctr.toFixed(2)}%
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm tabular-nums text-foreground">
                    {c.impressions > 0 ? `${(c.impressions / 1000).toFixed(0)}K` : '—'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm tabular-nums text-foreground">
                    {c.reach > 0 ? `${(c.reach / 1000).toFixed(0)}K` : '—'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="text-xs text-muted-foreground whitespace-nowrap">
                    <p>{c.startDate}</p>
                    <p>{c.endDate}</p>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <button
                      onClick={() => toast.info(`Editing "${c.name}"`)}
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
                      title="Edit campaign"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      onClick={() => toast.success(c.status === 'active' ? `"${c.name}" paused` : `"${c.name}" resumed`)}
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
                      title={c.status === 'active' ? 'Pause campaign' : 'Resume campaign'}
                    >
                      {c.status === 'active' ? <Pause size={12} /> : <Play size={12} />}
                    </button>
                    <button
                      onClick={() => toast.info(`Viewing campaign details`)}
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
                      title="View campaign details"
                    >
                      <ExternalLink size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border">
        <p className="text-xs text-muted-foreground">
          Showing {Math.min((page - 1) * PAGE_SIZE + 1, campaigns.length)}–{Math.min(page * PAGE_SIZE, campaigns.length)} of {campaigns.length} campaigns
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft size={14} />
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={`page-${i + 1}`}
              onClick={() => setPage(i + 1)}
              className={`w-7 h-7 rounded-lg text-xs font-medium transition-all ${page === i + 1 ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function SpendBar({ spend, budget }: { spend: number; budget: number }) {
  const pct = budget > 0 ? Math.min(100, (spend / budget) * 100) : 0;
  const color = pct >= 90 ? 'bg-negative' : pct >= 70 ? 'bg-warning' : 'bg-primary';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px] tabular-nums">
        <span className="text-muted-foreground">${spend.toLocaleString()}</span>
        <span className="text-muted-foreground">${budget.toLocaleString()}</span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full pacing-bar-fill ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}